import type {ReactElement} from 'react';
import {Icon} from '@Components/icon-v2';
import {IconSize} from '@Components/icon-v2/icon.types';
import {Text, TextSize} from '@Components/text';
import type {PlatformPostPreviewProps} from '@Components/social-media/components/social-post-content-multiposting/social-post-content-multiposting.types';
import {
  MIN_PAGES_FOR_MULTI_PAGE_LAYOUT,
  PreviewMediaLayout,
} from '@Components/social-media/components/social-post-content-multiposting/social-post-content-media-wrapper/social-post-content-media-wrapper.types';
import {PreviewAccountHeader} from '@Components/social-media/components/social-post-content-multiposting/components/preview-account-header/preview-account-header';
import {PreviewAccountMetaSkeleton} from '@Components/social-media/components/social-post-content-multiposting/components/preview-account-header/components/preview-account-meta-skeleton/preview-account-meta-skeleton';
import {PREVIEW_ACCOUNT_META_BAR_AND_SQUARE} from '@Components/social-media/components/social-post-content-multiposting/components/preview-account-header/components/preview-account-meta-skeleton/preview-account-meta-skeleton.types';
import {PreviewAccountMenuDots} from '@Components/social-media/components/social-post-content-multiposting/components/preview-account-header/components/preview-account-menu-dots/preview-account-menu-dots';
import styles from './instagram-post-preview.module.scss';
import {getCaptionWithPlaceholder, getReelMusicFromPublishingParams, getReelMusicLabel} from '@Libraries/social-media-params.library';
import {PreviewClampedCaption} from '@Components/social-media/components/social-post-content-multiposting/components/preview-clamped-caption/preview-clamped-caption';

export function InstagramPostPreview({account, platformMediaType, publishingParams, numberOfPages, renderMedia}: PlatformPostPreviewProps): ReactElement {
  const hasMultiplePages = (): boolean => {
    return numberOfPages >= MIN_PAGES_FOR_MULTI_PAGE_LAYOUT;
  };

  const captionText = getCaptionWithPlaceholder(publishingParams, account.id, platformMediaType);
  const reelMusic = getReelMusicFromPublishingParams(publishingParams[account.id]?.[platformMediaType] ?? null);

  const getHeaderMetaRow = (): ReactElement => {
    if (!reelMusic) {
      return <PreviewAccountMetaSkeleton parts={PREVIEW_ACCOUNT_META_BAR_AND_SQUARE} />;
    }

    return (
      <div className={styles.instagramReelMusic}>
        <Icon ariaHidden className={styles.instagramReelMusicIcon} icon="icon-music" size={IconSize.SIZE_ICON_8} />
        <Text className={styles.instagramReelMusicLabel} size={TextSize.XS} val={getReelMusicLabel(reelMusic)} />
      </div>
    );
  };

  const getMedia = (): ReactElement => {
    if (hasMultiplePages()) {
      return renderMedia({
        layout: PreviewMediaLayout.CAROUSEL,
        frameClassName: styles.instagramCarouselFrame,
        carouselClassName: styles.instagramCarousel,
        carouselCounterClassName: '_hidden',
      });
    }

    return renderMedia({layout: PreviewMediaLayout.SINGLE, frameClassName: styles.instagramMediaFrame});
  };

  const getReactionIcons = (): ReactElement => {
    return (
      <div className={styles.instagramReactions}>
        <Icon ariaHidden className={styles.instagramActionIcon} icon="icon-heart" size={IconSize.SIZE_ICON_16} />
        <Icon ariaHidden className={styles.instagramActionIcon} icon="icon-comment" size={IconSize.SIZE_ICON_16} />
        <Icon ariaHidden className={styles.instagramActionIcon} icon="icon-send" size={IconSize.SIZE_ICON_16} />
      </div>
    );
  };

  const getActionRow = (): ReactElement => {
    return (
      <div className={styles.instagramActionRow}>
        {getReactionIcons()}
        <Icon ariaHidden className={styles.instagramActionIcon} icon="icon-bookmark" size={IconSize.SIZE_ICON_16} />
      </div>
    );
  };

  const getCaption = (): ReactElement => {
    return (
      <div className={styles.instagramCaptionHolder}>
        <PreviewClampedCaption captionText={captionText} textClassName={styles.instagramCaptionClamp}>
          <Text bold className={styles.instagramCaptionAccountName} size={TextSize.XS} val={account.name} />{' '}
          <Text className={styles.instagramCaptionText} size={TextSize.XS} val={captionText} />
        </PreviewClampedCaption>
      </div>
    );
  };

  return (
    <div className={styles.instagramPostPreview}>
      <PreviewAccountHeader
        account={account}
        className={styles.instagramHeaderRow}
        metaRow={getHeaderMetaRow()}
        trailingAction={<PreviewAccountMenuDots />}
      />
      {getMedia()}
      {getActionRow()}
      {getCaption()}
    </div>
  );
}
